import React, { useEffect, useMemo, useState, useCallback, memo, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch
} from '@mui/material';
import MioOrario from './MioOrario';
import { authService } from '../api/authService';
import { dipendenteService, negozioService, bloccoService, coordinatoreOreLockService } from '../api/services';
import { Dipendente, Negozio, User, CoordinatoreOreLockStatus } from '../types/types';
import { Person, AccessTime, CalendarMonth, Clear, TableChart } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { Button as MuiButton } from '@mui/material';
import { formatDecimalHours } from '../utils/timeUtils';
import { apiRequest } from '../api/api';

const PAGE_COLOR = '#2196F3';
type MioOrarioStats = {
  oreContratto: number;
  orePianificate: number;
  oreConsuntivate: number;
  assenzeContrattuali: number;
  attuale: number;
  differenzaAttuale: number;
};

const TIPI_CONSOLIDAMENTO_OBBLIGATORIO = new Set([
  'LAVORATO',
  'ROL',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'LUTTO',
  'PERMESSI PARENTALI',
  'PERMESSI ELETTORALI',
  'PERMESSI STUDIO',
  'LEGGE 118 PERMESSO',
  'CORSI AI DIPENDENTI',
  'SMART WORKING',
]);

interface TurnoConsolidamentoRow {
  data: string;
  codiceTipoOrario?: string | null;
  oraPrevInizio?: string | null;
  oraPrevFine?: string | null;
  oraConsInizio?: string | null;
  oraConsFine?: string | null;
}

interface DataControlloSuggestion {
  suggestedDate: string;
  lastNonConsolidatoDate: string;
}

const isTurnoNonConsolidato = (turno: TurnoConsolidamentoRow): boolean => {
  if (!turno.codiceTipoOrario || !TIPI_CONSOLIDAMENTO_OBBLIGATORIO.has(turno.codiceTipoOrario)) return false;
  if (!turno.oraPrevInizio || !turno.oraPrevFine) return false;
  return !turno.oraConsInizio || !turno.oraConsFine;
};

const sortTurniByDateTime = (a: TurnoConsolidamentoRow, b: TurnoConsolidamentoRow): number => {
  const aDate = dayjs(`${a.data}T${(a.oraPrevInizio || '00:00').substring(0, 5)}`);
  const bDate = dayjs(`${b.data}T${(b.oraPrevInizio || '00:00').substring(0, 5)}`);
  if (aDate.isBefore(bDate)) return -1;
  if (aDate.isAfter(bDate)) return 1;
  return 0;
};

const buildDataControlloSuggestion = (
  turni: TurnoConsolidamentoRow[],
  dataInizio: string,
  dataFine: string
): DataControlloSuggestion | null => {
  const start = dayjs(dataInizio);
  const end = dayjs(dataFine);

  const nelPeriodo = turni.filter((t) => {
    const d = dayjs(t.data);
    return !d.isBefore(start, 'day') && !d.isAfter(end, 'day');
  });
  if (nelPeriodo.length === 0) return null;

  const nonConsolidati = nelPeriodo.filter(isTurnoNonConsolidato).sort(sortTurniByDateTime);
  if (nonConsolidati.length === 0) return null;

  // Se ci sono piu turni non consolidati, prendi il piu vecchio nel periodo
  // e proponi il giorno precedente.
  const primoNonConsolidato = nonConsolidati[0];
  const suggestedFallback = dayjs(primoNonConsolidato.data).subtract(1, 'day');

  return {
    suggestedDate: suggestedFallback.format('YYYY-MM-DD'),
    lastNonConsolidatoDate: primoNonConsolidato.data,
  };
};

// Componente separato per evitare re-render di MioOrario
interface DataControlloBoxProps {
  codicePersonale: string;
  nomeDipendente: string;
  initialDate: Dayjs | null;
  savedDate: Dayjs | null;
  initialWarning: boolean;
  isAdmin: boolean;
  isCoordinatore: boolean;
  meseVisualizzato: number; // 1-12
  annoVisualizzato: number;
  onSaved: (hasWarning: boolean, savedDate: Dayjs) => void;
  onCleared: () => void;
}

const DataControlloBox: React.FC<DataControlloBoxProps> = memo(({ 
  codicePersonale, 
  nomeDipendente, 
  initialDate, 
  savedDate,
  initialWarning,
  isAdmin,
  isCoordinatore,
  meseVisualizzato,
  annoVisualizzato,
  onSaved,
  onCleared
}) => {
  const [localDate, setLocalDate] = useState<Dayjs | null>(initialDate);
  const [saving, setSaving] = useState(false);
  const [hasWarning, setHasWarning] = useState(initialWarning);
  const [suggestionBanner, setSuggestionBanner] = useState<DataControlloSuggestion | null>(null);

  // Sincronizza con initialDate e initialWarning quando cambia dipendente
  useEffect(() => {
    setLocalDate(initialDate);
    setHasWarning(initialWarning);
    setSuggestionBanner(null);
  }, [initialDate, initialWarning]);

  const saveDataControllo = useCallback(async (dateToSave: Dayjs) => {
    const result = await bloccoService.setDataControllo(
      codicePersonale,
      dateToSave.format('YYYY-MM-DD'),
      meseVisualizzato,
      annoVisualizzato
    );
    const warning = result.hasLavoratoNonConsolidato === true;
    setHasWarning(warning);
    onSaved(warning, dateToSave);
    window.dispatchEvent(new CustomEvent('data-controllo-saved'));
    setLocalDate(dateToSave);
  }, [annoVisualizzato, codicePersonale, meseVisualizzato, onSaved]);

  const handleSave = async () => {
    if (!codicePersonale || !localDate) return;
    try {
      setSaving(true);

      if (isAdmin || isCoordinatore) {
        const dataInizioControllo = dayjs()
          .year(annoVisualizzato)
          .month(meseVisualizzato - 1)
          .startOf('month')
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD');
        const dataFineControllo = localDate.format('YYYY-MM-DD');
        const turni = await apiRequest<TurnoConsolidamentoRow[]>(
          `/orari-generati/codice/${encodeURIComponent(codicePersonale)}?dataInizio=${dataInizioControllo}&dataFine=${dataFineControllo}`
        );
        const suggestion = buildDataControlloSuggestion(turni || [], dataInizioControllo, dataFineControllo);
        if (suggestion && suggestion.suggestedDate !== dataFineControllo) {
          setSuggestionBanner(suggestion);
          return;
        }
      }

      await saveDataControllo(localDate);
      setSuggestionBanner(null);
    } catch (e) {
      console.error('Errore nel salvataggio della data di controllo', e);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSuggestedDate = async () => {
    if (!suggestionBanner) return;
    try {
      setSaving(true);
      const suggestedDayjs = dayjs(suggestionBanner.suggestedDate);
      await saveDataControllo(suggestedDayjs);
      setSuggestionBanner(null);
    } catch (e) {
      console.error('Errore nel salvataggio della data controllo suggerita', e);
    } finally {
      setSaving(false);
    }
  };

  const handleIgnoreSuggestionAndSave = async () => {
    if (!localDate) return;
    try {
      setSaving(true);
      await saveDataControllo(localDate);
      setSuggestionBanner(null);
    } catch (e) {
      console.error('Errore nel salvataggio della data controllo senza suggerimento', e);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!savedDate) return;
    try {
      setSaving(true);
      await bloccoService.clearDataControllo(codicePersonale, meseVisualizzato, annoVisualizzato);
      setHasWarning(false);
      setSuggestionBanner(null);
      onCleared();
      window.dispatchEvent(new CustomEvent('data-controllo-saved'));
    } catch (e) {
      console.error('Errore nella rimozione della data controllo', e);
    } finally {
      setSaving(false);
    }
  };

  const canClearSavedDate = !!savedDate
    && savedDate.year() === annoVisualizzato
    && savedDate.month() + 1 === meseVisualizzato;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
        Controllo Orari {hasWarning && <span title="Ci sono turni lavorativi senza ore consolidate">⚠️</span>}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
        <DatePicker
          label="Data Controllo"
          value={localDate}
          onChange={(val) => setLocalDate(val)}
          slotProps={{ 
            textField: { 
              size: 'small', 
              fullWidth: true
            } 
          }}
        />
      </LocalizationProvider>
      <Button
        variant="contained"
        disabled={!localDate || saving}
        onClick={handleSave}
        size="small"
        fullWidth
      >
        {saving ? 'Salvataggio...' : 'Imposta Data Controllo'}
      </Button>
      <Button
        variant="outlined"
        color="warning"
        disabled={!canClearSavedDate || saving}
        onClick={handleClear}
        size="small"
        fullWidth
      >
        Sblocca Data Controllo
      </Button>
      {isAdmin && (
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() => { window.location.href = '/controllo-orari'; }}
        >
          Controllo Orari
        </Button>
      )}
      <Dialog open={!!suggestionBanner} onClose={() => setSuggestionBanner(null)}>
        <DialogTitle>Attenzione</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {suggestionBanner
              ? `Ci sono dei turni non consolidati, l'ultimo turno è: ${dayjs(suggestionBanner.suggestedDate).format('DD/MM/YYYY')} (turno prima dell'ultimo non consolidato), vuoi impostare la data controllo a questo giorno?`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIgnoreSuggestionAndSave} color="warning" disabled={saving}>No</Button>
          <Button onClick={handleConfirmSuggestedDate} variant="contained" color="warning" disabled={saving}>Si</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

interface OrarioDipendenteProps {
  taste?: boolean;
}

const OrarioDipendente: React.FC<OrarioDipendenteProps> = ({ taste = false }) => {
  // Stabilize current user reference to avoid re-running effects on each render
  const currentUser: User | null = useMemo(() => authService.getCurrentUser(), []);
  const salitaAutomaticaStorageKey = `apporari-salita-automatica-${currentUser?.username || 'anonimo'}`;
  const location = useLocation();
  const navigate = useNavigate();
  const [negozi, setNegozi] = useState<Negozio[]>([]);
  const [loadingNegozi, setLoadingNegozi] = useState<boolean>(true);
  const [allDipendenti, setAllDipendenti] = useState<Dipendente[]>([]); // Tutti i dipendenti (non filtrati)
  const [loadingDipendenti, setLoadingDipendenti] = useState<boolean>(false);
  const [selectedNeg, setSelectedNeg] = useState<string>('');
  const [selectedDip, setSelectedDip] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [initialMonth, setInitialMonth] = useState<number | null>(null);
  const [initialYear, setInitialYear] = useState<number | null>(null);
  const [controlloDate, setControlloDate] = useState<Dayjs | null>(null);
  const [savedControlloDate, setSavedControlloDate] = useState<Dayjs | null>(null);
  const [controlloWarning, setControlloWarning] = useState<boolean>(false);
  const [salitaAutomatica, setSalitaAutomatica] = useState(() => {
    try {
      const saved = localStorage.getItem(salitaAutomaticaStorageKey);
      return saved === '1';
    } catch {
      return false;
    }
  });
  const [initialCodicePersonale, setInitialCodicePersonale] = useState<string>('');
  const [coordinatoreLock, setCoordinatoreLock] = useState<CoordinatoreOreLockStatus | null>(null);
  const [localLockStats, setLocalLockStats] = useState<{
    codicePersonale: string;
    mese: number;
    anno: number;
    differenzaAttuale: number;
  } | null>(null);
  const suppressLockAutoSelectRef = useRef(false);
  const suppressLockAutoSelectTimeoutRef = useRef<number | null>(null);
  const canManageDataControllo = currentUser?.ruolo === 'Admin' || currentUser?.ruolo === 'Coordinatore';

  // Mese e anno visualizzato nel calendario (default: mese corrente o da initialMonth/initialYear)
  const meseVisualizzato = initialMonth ?? dayjs().month() + 1; // 1-12
  const annoVisualizzato = initialYear ?? dayjs().year();

  // Callback per aggiornamento parziale dopo salvataggio DataControllo.
  const handleDataControlloSaved = useCallback((hasWarning: boolean, savedDate: Dayjs) => {
    setControlloWarning(hasWarning);
    setSavedControlloDate(savedDate);
    if (salitaAutomatica) {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }, [salitaAutomatica]);

  const handleDataControlloCleared = useCallback(() => {
    setControlloWarning(false);
    setSavedControlloDate(null);
  }, []);

  const handleSalitaAutomaticaChange = useCallback((checked: boolean) => {
    setSalitaAutomatica(checked);
    try {
      localStorage.setItem(salitaAutomaticaStorageKey, checked ? '1' : '0');
    } catch {}
  }, [salitaAutomaticaStorageKey]);

  const canAccess = useMemo(() => {
    return !!currentUser && (
      currentUser.ruolo === 'Admin' ||
      currentUser.ruolo === 'Coordinatore' ||
      currentUser.abilitaInsOrari === true
    );
  }, [currentUser?.ruolo, currentUser?.abilitaInsOrari]);


  const isCoordinatorLockActive = useMemo(() => {
    return currentUser?.ruolo === 'Coordinatore' && !!coordinatoreLock?.locked;
  }, [currentUser?.ruolo, coordinatoreLock?.locked]);

  const effectiveLockTargets = useMemo(() => {
    if (!isCoordinatorLockActive) return [];
    const allTargets = coordinatoreLock?.allTargets ?? (coordinatoreLock?.firstTarget ? [coordinatoreLock.firstTarget] : []);
    return allTargets
      .map(t => {
        const localMatch =
          localLockStats &&
          localLockStats.codicePersonale === t.codicePersonale &&
          localLockStats.mese === meseVisualizzato &&
          localLockStats.anno === annoVisualizzato;
        return localMatch ? { ...t, differenza: localLockStats.differenzaAttuale } : t;
      })
      .filter(t => t.differenza < 0);
  }, [isCoordinatorLockActive, coordinatoreLock, localLockStats, meseVisualizzato, annoVisualizzato]);

  const isCoordinatorLocked = useMemo(() => {
    return isCoordinatorLockActive && effectiveLockTargets.length > 0;
  }, [isCoordinatorLockActive, effectiveLockTargets.length]);

  // lockTarget: il target del dipendente attualmente visualizzato (se ha ore negative),
  // altrimenti il primo target della lista (per il redirect iniziale)
  const lockTarget = useMemo(() => {
    if (!isCoordinatorLocked) return null;
    const currentDip = allDipendenti.find((d: Dipendente) => d.nome === selectedDip);
    if (currentDip?.codicePersonale) {
      const matching = effectiveLockTargets.find(t => t.codicePersonale === currentDip.codicePersonale);
      if (matching) return matching;
    }
    return effectiveLockTargets[0] ?? null;
  }, [isCoordinatorLocked, effectiveLockTargets, allDipendenti, selectedDip]);

  // Filtra dipendenti in base al negozio selezionato (e alle ore negative se locked)
  const dipendentiFiltrati = useMemo(() => {
    const allTargets = isCoordinatorLocked ? effectiveLockTargets : [];
    const allTargetsCodes = new Set(allTargets.map(t => t.codicePersonale));
    const base = isCoordinatorLocked
      ? allDipendenti.filter(d => d.codicePersonale && allTargetsCodes.has(d.codicePersonale))
      : allDipendenti;
    if (!selectedNeg) return base;
    return base.filter(d => d.neg === selectedNeg);
  }, [allDipendenti, selectedNeg, isCoordinatorLocked, effectiveLockTargets]);

  const negoziVisibili = useMemo(() => {
    if (!isCoordinatorLocked || !coordinatoreLock?.allowedNegozi?.length) return negozi;
    return negozi.filter(n => coordinatoreLock.allowedNegozi.includes(n.neg));
  }, [negozi, isCoordinatorLocked, coordinatoreLock]);

  // Handler per cambio negozio - SEMPLIFICATO per evitare cicli di sincronizzazione
  const handleNegozioChange = useCallback((negozio: string) => {
    setSelectedNeg(negozio);
    
    // Auto-seleziona il primo dipendente disponibile per il negozio scelto
    const allTargets = isCoordinatorLocked
      ? effectiveLockTargets
      : [];
    const allTargetsCodes = new Set(allTargets.map(t => t.codicePersonale));
    
    const base = isCoordinatorLocked
      ? allDipendenti.filter(d => d.codicePersonale && allTargetsCodes.has(d.codicePersonale))
      : allDipendenti;
    
    const filtered = base.filter(d => d.neg === negozio);
    if (filtered.length > 0) {
      const firstDip = filtered[0];
      // Imposta direttamente sia selectedDip che initialCodicePersonale
      // NON aspettare l'useEffect di sincronizzazione per evitare cicli
      setSelectedDip(firstDip.nome);
      // NB: NON settare initialCodicePersonale qui - viene settato via query params al mount
    }
  }, [isCoordinatorLocked, effectiveLockTargets, allDipendenti]);

  // Handler per cambio dipendente
  const handleDipendenteChange = useCallback((nomeDipendente: string) => {
    setSelectedDip(nomeDipendente);
    // Trova il negozio del dipendente e precompila la select negozio
    const dip = allDipendenti.find(d => d.nome === nomeDipendente);
    if (dip && dip.neg) {
      setSelectedNeg(dip.neg);
    }
    // NOTA: NON settare initialCodicePersonale qui!
    // initialCodicePersonale è usato SOLO per sincronizzare i query params al mount.
    // Settarlo qui triggerava il redirect useEffect e causava rimount di MioOrario.
  }, [allDipendenti]);

  // Handler per cancellare negozio
  const handleClearNegozio = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNeg('');
  }, []);

  // Handler per cancellare dipendente
  const handleClearDipendente = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDip('');
  }, []);

  // Carica tutti i dipendenti all'inizio (per permettere selezione libera)
  useEffect(() => {
    const loadAllDipendenti = async () => {
      if (!currentUser) return;
      const isPrivileged = currentUser.ruolo === 'Admin' || currentUser.ruolo === 'Coordinatore';
      
      setLoadingDipendenti(true);
      setError(null);
      try {
        if (isPrivileged) {
          // ✅ OTTIMIZZAZIONE: Usa endpoint /dipendenti che restituisce già tutti i dipendenti
          // filtrati per ruolo (Admin: tutti, Coordinatore: solo i suoi negozi)
          // Evita N chiamate (una per negozio)
          const allDeps = await dipendenteService.getAll();
          // Ordina alfabeticamente
          allDeps.sort((a, b) => a.nome.localeCompare(b.nome));
          setAllDipendenti(allDeps);
        } else if (currentUser.abilitaInsOrari && currentUser.negozio) {
          // Utente non privilegiato: carica solo dipendenti del proprio negozio
          const deps = await dipendenteService.getByNegozio(currentUser.negozio);
          deps.sort((a, b) => a.nome.localeCompare(b.nome));
          setAllDipendenti(deps);
        }
      } catch (e) {
        console.error('Errore caricamento dipendenti', e);
        setError('Errore nel caricamento dei dipendenti');
      } finally {
        setLoadingDipendenti(false);
      }
    };

    if (canAccess) {
      loadAllDipendenti();
    }
  }, [canAccess, currentUser?.ruolo, currentUser?.negozio]);

  useEffect(() => {
  const loadNegozi = async () => {
      if (!currentUser) return;
      setLoadingNegozi(true);
      setError(null);
      try {
        let list: Negozio[] = [];
  const isPrivileged = currentUser.ruolo === 'Admin' || currentUser.ruolo === 'Coordinatore';
  // Backend filtra già per ruolo su GET /negozi per i privilegiati.
  // Per utenti abilitati (abilitaInsOrari) non privilegiati, limita al solo negozio dell'utente.
  if (isPrivileged) {
          list = await negozioService.getAll();
        } else if (currentUser.abilitaInsOrari) {
          if (!currentUser.negozio) {
            throw new Error('Nessun negozio associato all’utente');
          }
          const negozio = await negozioService.getById(currentUser.negozio);
          list = [negozio];
        } else {
          // Fallback: nessun negozio disponibile per utenti non abilitati
          list = [];
        }
        setNegozi(list);
        // Leggi eventuali parametri query
        const params = new URLSearchParams(location.search);
        const qNeg = params.get('neg');
        const qDip = params.get('dip');
        const qCp = params.get('cp');
        const qMese = params.get('mese');
        const qAnno = params.get('anno');

  const parsedMese = qMese ? parseInt(qMese, 10) : NaN;
  const parsedAnno = qAnno ? parseInt(qAnno, 10) : NaN;
  if (!isNaN(parsedMese) && parsedMese >= 1 && parsedMese <= 12) setInitialMonth(parsedMese);
  if (!isNaN(parsedAnno) && parsedAnno >= 1900 && parsedAnno <= 3000) setInitialYear(parsedAnno);

        // Preselezione negozio: priorità ai query param
        if (qNeg && list.some(n => n.neg === qNeg)) {
          setSelectedNeg(qNeg);
        } else if (currentUser.negozio && list.some(n => n.neg === currentUser.negozio)) {
          setSelectedNeg(prev => (prev || currentUser.negozio!));
        }
        
        // Se c'è dipendente nei query params, lo impostiamo
        if (qDip) {
          setSelectedDip(qDip);
        }
        if (qCp) {
          setInitialCodicePersonale(qCp);
        }
      } catch (e) {
        console.error('Errore caricamento negozi', e);
        setError('Errore nel caricamento dei negozi');
      } finally {
        setLoadingNegozi(false);
      }
    };

    if (canAccess) {
      loadNegozi();
    }
  }, [canAccess, currentUser?.ruolo, currentUser?.nome, currentUser?.negozio, location.search]);

  // Sincronizza il CP passato via query params con selectedDip e selectedNeg
  // Questo viene eseguito DOPO il caricamento dei dipendenti
  useEffect(() => {
    if (!initialCodicePersonale || allDipendenti.length === 0) return;
    
    const dip = allDipendenti.find(d => d.codicePersonale === initialCodicePersonale);
    if (!dip) return;
    
    // Aggiorna selectedDip e selectedNeg solo se sono diversi
    if (selectedDip !== dip.nome) {
      setSelectedDip(dip.nome);
    }
    if (selectedNeg !== dip.neg) {
      setSelectedNeg(dip.neg);
    }
  }, [initialCodicePersonale, allDipendenti]);

  // Auto-seleziona il primo dipendente quando il negozio cambia (se non è già impostato)
  useEffect(() => {
    if (!selectedNeg || allDipendenti.length === 0) return;
    if (selectedDip && selectedDip.trim()) return; // Non cambiare se già impostato
    
    // Filtra i dipendenti per il negozio selezionato
    const allTargets = isCoordinatorLocked
      ? effectiveLockTargets
      : [];
    const allTargetsCodes = new Set(allTargets.map(t => t.codicePersonale));
    
    const base = isCoordinatorLocked
      ? allDipendenti.filter(d => d.codicePersonale && allTargetsCodes.has(d.codicePersonale))
      : allDipendenti;
    
    const filtered = base.filter(d => d.neg === selectedNeg);
    
    // Se ci sono dipendenti, seleziona il primo
    if (filtered.length > 0) {
      const firstDip = filtered[0];
      setSelectedDip(firstDip.nome);
      console.log(`[OrarioDipendente] Auto-selezionato dipendente: ${firstDip.nome} per negozio: ${selectedNeg}`);
    }
  }, [selectedNeg, allDipendenti, isCoordinatorLocked, effectiveLockTargets]);

  // Carica eventuale DataControllo del dipendente selezionato (solo per Admin/Coordinatore)
  // Mostra sempre la data odierna come default nel DatePicker
  useEffect(() => {
    const loadBlocco = async () => {
      try {
        if (!canManageDataControllo) return;
        const dip = allDipendenti.find((d: Dipendente) => d.nome === selectedDip);
        if (!dip?.codicePersonale) { 
          setControlloDate(null); 
          setSavedControlloDate(null);
          setControlloWarning(false);
          return; 
        }
        const b = await bloccoService.getByCodice(dip.codicePersonale, meseVisualizzato, annoVisualizzato);
        
        // Imposta sempre la data odierna nel DatePicker
        setControlloDate(dayjs());
        
        // Ma verifica il warning in base alla data salvata nel DB (se presente)
        if (b && b.dataControllo) {
          setSavedControlloDate(dayjs(b.dataControllo));
          setControlloWarning(b.hasLavoratoNonConsolidato === true);
        } else {
          setSavedControlloDate(null);
          setControlloWarning(false);
        }
      } catch (e) {
        console.error('Errore caricamento blocco:', e);
        // In caso di errore, usa comunque la data odierna
        setControlloDate(dayjs());
        setSavedControlloDate(null);
        setControlloWarning(false);
      }
    };
    loadBlocco();
  }, [canManageDataControllo, allDipendenti, selectedDip, meseVisualizzato, annoVisualizzato]);

  useEffect(() => {
    const checkLock = async () => {
      if (currentUser?.ruolo !== 'Coordinatore') {
        setCoordinatoreLock(null);
        return;
      }
      try {
        const lock = await coordinatoreOreLockService.getStatus();
        setCoordinatoreLock(lock);
      } catch (e) {
        console.error('Errore caricamento lock coordinatore:', e);
      }
    };
    checkLock();
  }, [currentUser?.ruolo, location.search]);

  // Listener per aggiornamento real-time del lock status quando il cache viene invalidato
  // IMPORTANTE: Non triggerare refetch durante il salvataggio turni per evitare rimount non intenzionali
  useEffect(() => {
    if (currentUser?.ruolo !== 'Coordinatore') return;

    const handleLockCacheInvalidated = async (event: Event) => {
      try {
        const detail = (event as CustomEvent<{ url?: string }>).detail;
        const isLocalOrariWrite = location.pathname.startsWith('/orario-dipendente') && detail?.url?.includes('/orari-generati');
        if (isLocalOrariWrite) {
          suppressLockAutoSelectRef.current = true;
          if (suppressLockAutoSelectTimeoutRef.current !== null) {
            window.clearTimeout(suppressLockAutoSelectTimeoutRef.current);
          }
          suppressLockAutoSelectTimeoutRef.current = window.setTimeout(() => {
            suppressLockAutoSelectRef.current = false;
            suppressLockAutoSelectTimeoutRef.current = null;
          }, 3000);
          console.log('[OrarioDipendente] Write locale: skip auto-select lock, refresh parziale gestito da MioOrario');
          return;
        }
        console.log('[OrarioDipendente] Event ricevuto - refetching lock status');
        const newLock = await coordinatoreOreLockService.getStatus({ force: true });
        
        // Confronta i targets: se il dipendente corrente è ANCORA nei nuovi targets,
        // non aggiornare lo state (evita rimount non intenzionali)
        const currentCp = allDipendenti.find((d: Dipendente) => d.nome === selectedDip)?.codicePersonale;
        const newTargetsCodes = new Set(
          (newLock?.allTargets ?? (newLock?.firstTarget ? [newLock.firstTarget] : []))
            .map(t => t.codicePersonale)
        );
        
        // Se il dipendente corrente è ancora nei target, il coordinatore sta ancora lavorando su di lui
        // → non aggiornare lo state
        if (newLock?.locked && currentCp && newTargetsCodes.has(currentCp)) {
          console.log('[OrarioDipendente] Dipendente corrente ancora nei targets, skip state update');
          return;
        }
        
        // Altrimenti aggiorna (dipendente è stato rimosso dai target o lock è false)
        setCoordinatoreLock(newLock);
        console.log('[OrarioDipendente] Lock status aggiornato:', newLock.locked);
      } catch (e) {
        console.error('[OrarioDipendente] Errore refetch lock:', e);
      }
    };

    window.addEventListener('coordinatore-ore-lock-cache-invalidated', handleLockCacheInvalidated);
    console.log('[OrarioDipendente] Listener registrato per coordinatore-ore-lock-cache-invalidated');

    return () => {
      window.removeEventListener('coordinatore-ore-lock-cache-invalidated', handleLockCacheInvalidated);
      if (suppressLockAutoSelectTimeoutRef.current !== null) {
        window.clearTimeout(suppressLockAutoSelectTimeoutRef.current);
      }
    };
  }, [currentUser?.ruolo, allDipendenti, selectedDip, location.pathname]);

  useEffect(() => {
    if (!isCoordinatorLocked) return;
    if (suppressLockAutoSelectRef.current) return;
    // Aspetta che i dipendenti siano caricati prima di valutare
    if (loadingDipendenti || allDipendenti.length === 0) return;
    const allTargetsCodes = new Set(effectiveLockTargets.map(t => t.codicePersonale));
    // Prova prima con initialCodicePersonale (settato dai query params prima del caricamento),
    // poi con il dipendente già selezionato
    const currentCp =
      (initialCodicePersonale && initialCodicePersonale.trim() ? initialCodicePersonale : null) ??
      allDipendenti.find((d: Dipendente) => d.nome === selectedDip)?.codicePersonale;
    // Se il dipendente corrente ha ore negative, lascia navigare liberamente
    if (currentCp && allTargetsCodes.has(currentCp)) return;
    // Altrimenti forza al primo target non risolto
    const first = effectiveLockTargets[0];
    if (!first) return;
    if (selectedNeg !== first.negozio) {
      setSelectedNeg(first.negozio);
    }
    const dipByCode = allDipendenti.find((d: Dipendente) => d.codicePersonale === first.codicePersonale);
    if (dipByCode && selectedDip !== dipByCode.nome) {
      setSelectedDip(dipByCode.nome);
    }
  }, [isCoordinatorLocked, effectiveLockTargets, allDipendenti, loadingDipendenti, initialCodicePersonale, selectedNeg, selectedDip]);

  const selectedDipendente = allDipendenti.find((d: Dipendente) => d.nome === selectedDip);
  const selectedDipendenteNome = selectedDipendente?.nome;
  const selectedDipendenteCodice = selectedDipendente?.codicePersonale;

  const mioOrarioOverrideUser = useMemo(() => {
    if (!selectedDipendenteNome || !selectedDipendenteCodice) return null;
    return {
      nome: selectedDipendenteNome,
      ruolo: currentUser?.ruolo || 'Dipendente',
      oldId: selectedDipendenteCodice
    } as any;
  }, [selectedDipendenteNome, selectedDipendenteCodice, currentUser?.ruolo]);

  const handleMioOrarioStatsChange = useCallback((stats: MioOrarioStats) => {
    if (!selectedDipendenteCodice) return;
    setLocalLockStats({
      codicePersonale: selectedDipendenteCodice,
      mese: meseVisualizzato,
      anno: annoVisualizzato,
      differenzaAttuale: stats.differenzaAttuale
    });
  }, [selectedDipendenteCodice, meseVisualizzato, annoVisualizzato]);

  if (!canAccess) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Accesso non consentito</Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: taste ? { xs: 0, md: 2 } : 2,
        mb: 4,
        py: taste ? { xs: 2, md: 3 } : 0,
        minHeight: taste ? '100dvh' : undefined,
        backgroundColor: taste ? '#f7f8fa' : undefined,
      }}
    >
      <Box sx={{
        mb: 3,
        textAlign: taste ? 'left' : 'center',
        position: 'relative',
        display: taste ? 'grid' : 'block',
        gridTemplateColumns: taste ? { xs: '1fr', md: '1fr auto' } : undefined,
        alignItems: 'end',
        gap: taste ? 2 : undefined,
        pb: taste ? 2.5 : 0,
        borderBottom: taste ? '1px solid #d9dee7' : undefined,
      }}>
        <FormControlLabel
          control={<Switch checked={salitaAutomatica} onChange={(_, checked) => handleSalitaAutomaticaChange(checked)} />}
          label="Salita automatica"
          sx={taste
            ? { m: 0, justifySelf: { xs: 'start', md: 'end' }, color: 'text.secondary' }
            : { position: 'absolute', top: 0, right: 16, m: 0, transform: 'scale(1.1)', transformOrigin: 'top right' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: taste ? 'flex-start' : 'center', mb: taste ? 0 : 2, gridRow: taste ? { xs: 1, md: 'auto' } : undefined }}>
          <Box sx={{
            position: 'relative',
            display: 'inline-flex',
            mr: 2,
            p: taste ? 1 : 0,
            borderRadius: taste ? 2 : 0,
            backgroundColor: taste ? '#e8f0fe' : 'transparent',
          }}>
            <Person fontSize="large" sx={{ color: PAGE_COLOR }} />
            <AccessTime sx={{ position: 'absolute', right: taste ? 2 : -6, bottom: taste ? 2 : -4, fontSize: 18, color: '#1f2937' }} />
          </Box>
          <Box>
            <Typography variant={taste ? 'h4' : 'h4'} component="h1" sx={taste ? { fontWeight: 700, letterSpacing: '-0.02em', color: '#172033', lineHeight: 1.1 } : undefined}>
              Orario dipendenti
            </Typography>
            {taste && (
              <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                Consulta, aggiorna e verifica il mese della persona selezionata.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Paper sx={{
        p: taste ? { xs: 2, md: 2.5 } : 2,
        mb: 3,
        borderRadius: taste ? 3 : 1,
        border: taste ? '1px solid #dfe4ec' : undefined,
        boxShadow: taste ? '0 12px 30px rgba(31, 41, 55, 0.08)' : undefined,
      }}>
        {isCoordinatorLocked && lockTarget && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Operativita limitata: devi sistemare le ore negative di {lockTarget.fullName || `${lockTarget.nome} ${lockTarget.cognome}`.trim()} (negozio {lockTarget.negozio}, differenza: {formatDecimalHours(lockTarget.differenza)}). Accesso limitato ai negozi autorizzati.
          </Alert>
        )}
        {taste && (
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#374151' }}>
            Selezione calendario
          </Typography>
        )}
        <Box sx={{ display: taste ? 'grid' : 'flex', gridTemplateColumns: taste ? { xs: '1fr', md: 'minmax(240px, 0.9fr) minmax(300px, 1.1fr)' } : undefined, flexWrap: 'wrap', gap: 2 }}>
          <FormControl
            sx={{ minWidth: 240 }}
            size="small"
            disabled={loadingNegozi || !canManageDataControllo}
          >
            <InputLabel id="sel-neg-label">Negozio</InputLabel>
            <Select
              labelId="sel-neg-label"
              value={selectedNeg}
              label="Negozio"
              onChange={(e) => handleNegozioChange(e.target.value as string)}
              endAdornment={
                selectedNeg && (
                  <IconButton
                    size="small"
                    onClick={handleClearNegozio}
                    sx={{ mr: 2 }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                )
              }
            >
              {loadingNegozi && <MenuItem value=""><em><CircularProgress size={18} sx={{ mr: 1 }} /> Caricamento...</em></MenuItem>}
              {!loadingNegozi && negoziVisibili.length === 0 && (
                <MenuItem value=""><em>Nessun negozio disponibile</em></MenuItem>
              )}
              {negoziVisibili.map(n => (
                <MenuItem key={n.neg} value={n.neg}>{n.neg} - {n.negozio}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 280 }} size="small">
            <InputLabel id="sel-dip-label">Dipendente</InputLabel>
            <Select
              labelId="sel-dip-label"
              value={selectedDip}
              label="Dipendente"
              onChange={(e) => handleDipendenteChange(e.target.value as string)}
              endAdornment={
                selectedDip && (
                  <IconButton
                    size="small"
                    onClick={handleClearDipendente}
                    sx={{ mr: 2 }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                )
              }
            >
              {loadingDipendenti && <MenuItem value=""><em><CircularProgress size={18} sx={{ mr: 1 }} /> Caricamento...</em></MenuItem>}
              {!loadingDipendenti && dipendentiFiltrati.length === 0 && (
                <MenuItem value=""><em>Nessun dipendente disponibile</em></MenuItem>
              )}
              {dipendentiFiltrati.map((d: Dipendente) => (
                <MenuItem key={d.codicePersonale} value={d.nome}>{d.nome} ({d.neg})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/* Pulsante navigazione a Generazione Orari con parametri negozio/mese/anno correnti */}
        {selectedNeg && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={taste ? <CalendarMonth /> : undefined}
              onClick={() => {
                // Usa selectedDate da MioOrario se disponibile tramite initialMonth/initialYear o fallback mese corrente
                const mese = (initialMonth && initialMonth >= 1 && initialMonth <= 12) ? initialMonth : dayjs().month() + 1;
                const anno = (initialYear && initialYear >= 1900) ? initialYear : dayjs().year();
                const base = `/generazione-orario?neg=${encodeURIComponent(selectedNeg)}&mese=${mese}&anno=${anno}`;
                window.location.href = base;
              }}
              size="small"
            >
              Vai a Generazione Orari
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<TableChart />}
              disabled={isCoordinatorLocked}
              onClick={() => {
                const mese = (initialMonth && initialMonth >= 1 && initialMonth <= 12) ? initialMonth : dayjs().month() + 1;
                const anno = (initialYear && initialYear >= 1900) ? initialYear : dayjs().year();
                const base = `/orario-dipendente-compatto?neg=${encodeURIComponent(selectedNeg)}&dip=${encodeURIComponent(selectedDip)}&mese=${mese}&anno=${anno}`;
                navigate(base);
              }}
              size="small"
            >
              Vista Compatta
            </Button>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

    {selectedNeg && selectedDipendente && mioOrarioOverrideUser && (
      <Box sx={taste ? {
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        border: '1px solid #dfe4ec',
        boxShadow: '0 12px 30px rgba(31, 41, 55, 0.08)',
      } : undefined}>
        <MioOrario
      key={`${selectedNeg}-${selectedDipendente.nome}-${initialYear ?? 'y'}-${initialMonth ?? 'm'}`}
          showCambiTurno={false}
          hideHeader={true}
          overrideUser={mioOrarioOverrideUser}
      initialMonth={initialMonth ?? undefined}
      initialYear={initialYear ?? undefined}
          dataControlloOverride={savedControlloDate ? savedControlloDate.format('YYYY-MM-DD') : null}
          onStatsChange={handleMioOrarioStatsChange}
          additionalCalendarContent={
            canManageDataControllo && selectedDipendente ? (
              <DataControlloBox
                codicePersonale={selectedDipendente.codicePersonale}
                nomeDipendente={selectedDipendente.nome}
                initialDate={controlloDate}
                savedDate={savedControlloDate}
                initialWarning={controlloWarning}
                isAdmin={currentUser?.ruolo === 'Admin'}
                isCoordinatore={currentUser?.ruolo === 'Coordinatore'}
                meseVisualizzato={meseVisualizzato}
                annoVisualizzato={annoVisualizzato}
                onSaved={handleDataControlloSaved}
                onCleared={handleDataControlloCleared}
              />
            ) : undefined
          }
          alwaysShowPreferences={
            currentUser?.ruolo === 'Admin' ||
            currentUser?.ruolo === 'Coordinatore' ||
            currentUser?.abilitaInsOrari === true
          }
        />
      </Box>
      )}
    </Container>
  );
};

export default OrarioDipendente;
